/**
 * Pure KYC gate decision table for the PWA.
 *
 * Mirrors mobile `KycRequiredGuard._blockedRoute`
 * (apps/mobile/lib/features/auth/utils/kyc_required_guard.dart) exactly:
 *
 * - Admin / SuperAdmin bypass entirely (role `2`, `3`, `admin`, `superadmin`,
 *   case-insensitive; integer serialisation `>= 2`).
 * - Client accounts never need KYC.
 * - Creator / Agency with Verified (`2`/`verified`) or Pending (`1`/`pending`)
 *   are allowed (pending is not redirected — the user already submitted).
 * - Anything else (None `0`, Rejected `3`, names, `''`, unparseable) is blocked.
 */
export type KycGuardUser = {
  role: string
  accountType: string
  kycStatus: string
}

export const KYC_ONBOARDING_ROUTE = '/kyc-onboarding' as const

export function kycGuard(user: KycGuardUser): typeof KYC_ONBOARDING_ROUTE | null {
  if (isAdminRole(user.role)) return null
  if (user.accountType === 'client') return null
  const kyc = kycStatusValue(user.kycStatus)
  if (kyc === 2) return null
  if (kyc === 1) return null
  return KYC_ONBOARDING_ROUTE
}

/**
 * True when the role string is Admin / SuperAdmin (case-insensitive),
 * regardless of whether the backend serialised it as an int or as the
 * C# enum name.
 */
export function isAdminRole(role: string): boolean {
  if (!role) return false
  const lower = role.toLowerCase()
  if (lower === 'admin' || lower === 'superadmin') return true
  const asInt = parseInt(lower, 10)
  return !Number.isNaN(asInt) && String(asInt) === lower && asInt >= 2
}

/**
 * Dual-shape tolerance for KycStatus. Returns -1 when the value is
 * unparseable so callers treat "unknown" as the strictest case (blocked),
 * matching mobile.
 */
export function kycStatusValue(status: string): number {
  if (!status) return -1
  const lower = status.toLowerCase()
  switch (lower) {
    case 'verified':
      return 2
    case 'pending':
      return 1
    case 'rejected':
      return 3
    case 'none':
      return 0
    default:
      break
  }
  const asInt = parseInt(lower, 10)
  return !Number.isNaN(asInt) && String(asInt) === lower ? asInt : -1
}
